import { IconWrapper, Loader, Pagination } from "@caprikorn/dynamo-ui";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";

import { ErrorTemplate } from "@components/ErrorTemplate/ErrorTemplate";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { computeRoute } from "../SearchDrawer.utils";
import classes from "./SearchResults.module.css";
import type { SearchResultsProps } from "./SearchResults.types";
import { SearchResultCard } from "../SearchResultCard/SearchResultCard";

export const SearchResults: FC<SearchResultsProps> = ({
  level,
  toggleDrawer,
  data,
  submitPageChange,
  submitSizeChange,
  isLoading,
  isError,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const navigate = useNavigate();

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <ErrorTemplate title={t("form.error.generic", { ns: "common" })} />;
  }

  if (!data) {
    return null;
  }

  if (data.pagination.recordsCount === 0) {
    return (
      <div className={classes.emptySearch}>
        <IconWrapper role="presentation" size="4xl" src="errorFix" />
        <h2 className="font-title-md">{t("searchDrawer.emptyState.title")}</h2>
        <p className="font-body-highlight">
          {t(`searchDrawer.emptyState.${level}.subtitle`)}
        </p>
        <p>{t(`searchDrawer.emptyState.${level}.paragraph`)}</p>
        <ul className={classes.list}>
          <li>
            <p className="font-body-highlight">
              {t(`searchDrawer.emptyState.${level}.listItem1.label`)}
            </p>
            <p>{t(`searchDrawer.emptyState.${level}.listItem1.extraInfo`)}</p>
          </li>
          <li>
            <span className="font-body-highlight">
              {t(`searchDrawer.emptyState.${level}.listItem2.label`)}
            </span>
            {t(`searchDrawer.emptyState.${level}.listItem2.parenthesis`)}
          </li>
        </ul>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.filter}>
        <p className="font-caption">
          {t("searchDrawer.result.total", {
            count: data.pagination.recordsCount,
          })}
        </p>
      </div>

      <div className={classes.results}>
        {data.itemList.map((item) => (
          <SearchResultCard
            key={item.name}
            data={item.data}
            onClick={() => {
              toggleDrawer();
              navigate(computeRoute({ refs: item.references, level }));
            }}
            subtitle={item.references[level] || ""}
            title={item.name}
          />
        ))}
      </div>

      {data.pagination.size > 0 && (
        <Pagination
          currentPage={data.pagination.currentPage}
          currentPageSize={data.pagination.size}
          onPageChange={submitPageChange}
          onPageSizeChange={submitSizeChange}
          pageSizes={[10, 20, 30]}
          totalPages={Math.ceil(
            data.pagination.recordsCount / data.pagination.size,
          )}
          withPageSizeSelector
        />
      )}
    </div>
  );
};
